
import { v4 as uuidv4 } from 'uuid';
import { Admin, AdminHistory } from '@/types/admin.types';
import { universalStorage } from '../../storage/universalStorage';
import { storageCompression } from '../../../utils/storageCompression';

export class AdminFileStorageService {
  private cache = new Map<string, Admin>();
  private cacheTimestamp = 0;
  private readonly CACHE_DURATION = 30000; // 30 seconds
  private readonly STORAGE_KEY = 'nalc_admins';
  private readonly BATCH_SIZE = 50; // Process admins in batches
  private readonly MAX_RETRIES = 3;

  constructor() {
    // Enable compression for unlimited capacity
    storageCompression.enableCompression(true);
    storageCompression.setCompressionLevel(8);
  }

  async getAllAdmins(): Promise<Admin[]> {
    try {
      // Check cache first
      if (this.isCacheValid()) {
        const cachedAdmins = Array.from(this.cache.values());
        if (cachedAdmins.length > 0) {
          console.log(`📋 Retrieved ${cachedAdmins.length} admins from cache`);
          return cachedAdmins;
        }
      }

      console.log('📋 Loading admins with unlimited capacity optimization...');
      
      // Load with compression support for unlimited capacity
      const compressedData = await universalStorage.getItem<string>(this.STORAGE_KEY);
      
      let admins: Admin[] = [];
      
      if (compressedData) {
        try {
          // Try decompressing first
          if (typeof compressedData === 'string' && compressedData.includes('__COMPRESSED__')) {
            admins = storageCompression.decompressData(compressedData);
            console.log('📦 Successfully decompressed admin data for unlimited capacity');
          } else {
            // Handle both compressed and uncompressed data
            admins = typeof compressedData === 'string' ? JSON.parse(compressedData) : compressedData;
          }
        } catch (parseError) {
          console.warn('Failed to parse admin data, trying direct access:', parseError);
          // Fallback to direct localStorage access
          const rawData = localStorage.getItem(this.STORAGE_KEY);
          if (rawData) {
            admins = JSON.parse(rawData);
          }
        }
      }

      // Ensure admins is always an array
      if (!Array.isArray(admins)) {
        console.warn('Admin data is not an array, initializing empty array');
        admins = [];
      }

      // Validate and clean admin data
      admins = admins.filter(admin => admin && admin.id).map(admin => ({
        ...admin,
        id: admin.id || `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        firstName: admin.firstName || '',
        lastName: admin.lastName || '',
        email: admin.email || '',
        phone: admin.phone || '',
        position: admin.position || 'Admin',
        role: admin.role || 'Admin',
        status: admin.status || 'Active',
        joinDate: admin.joinDate || new Date().toISOString().split('T')[0],
        createdAt: admin.createdAt || new Date().toISOString(),
        updatedAt: admin.updatedAt || new Date().toISOString(),
        lastModified: admin.lastModified || new Date().toISOString(),
        syncStatus: admin.syncStatus || 'synced',
        history: admin.history || []
      }));

      // Update cache for unlimited capacity
      this.updateCache(admins);
      
      console.log(`✅ Loaded ${admins.length} admins with unlimited capacity support`);
      return admins;
    } catch (error) {
      console.error('Error loading admins with unlimited capacity:', error);
      
      // Try emergency recovery
      try {
        const emergencyData = localStorage.getItem(this.STORAGE_KEY);
        if (emergencyData) {
          const recoveredAdmins = JSON.parse(emergencyData);
          if (Array.isArray(recoveredAdmins)) {
            console.log('🚨 Emergency recovery successful');
            return recoveredAdmins;
          }
        }
      } catch (recoveryError) {
        console.error('Emergency recovery failed:', recoveryError);
      }
      
      return [];
    }
  }

  async getAdminById(id: string): Promise<Admin | null> {
    try {
      const admins = await this.getAllAdmins();
      return admins.find(admin => admin.id === id) || null;
    } catch (error) {
      console.error(`Error getting admin by ID ${id}:`, error);
      return null;
    }
  }

  async saveAdmin(admin: Admin): Promise<Admin> {
    try {
      if (admin.id) {
        // Update existing admin
        return await this.updateAdmin(admin.id, admin) || admin;
      } else {
        // Create new admin
        return await this.addAdmin(admin);
      }
    } catch (error) {
      console.error('Error saving admin:', error);
      throw error;
    }
  }

  async addAdmin(admin: Omit<Admin, 'id'> | Admin): Promise<Admin> {
    let retries = 0;
    
    while (retries < this.MAX_RETRIES) {
      try {
        console.log('💾 Adding admin with unlimited capacity optimization...');
        
        const allAdmins = await this.getAllAdmins();
        
        // Generate sequential ID for better organization
        const maxId = allAdmins.reduce((max, a) => {
          const numId = parseInt(a.id.replace(/\D/g, '')) || 0;
          return Math.max(max, numId);
        }, 0);
        
        // Fix: Handle both types properly
        const adminId = 'id' in admin && admin.id ? admin.id : `admin_${maxId + 1}_${Date.now()}`;
        
        const newAdmin: Admin = {
          ...admin,
          id: adminId,
          createdAt: 'createdAt' in admin && admin.createdAt ? admin.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          syncStatus: 'synced'
        } as Admin;

        // Add to array
        allAdmins.push(newAdmin);
        
        // Save with compression for unlimited capacity
        await this.saveAdminsWithCompression(allAdmins);
        
        // Update cache
        this.cache.set(newAdmin.id, newAdmin);
        
        console.log(`✅ Admin added successfully: ${newAdmin.id} (Total: ${allAdmins.length})`);
        return newAdmin;
        
      } catch (error) {
        retries++;
        console.error(`Attempt ${retries} failed to add admin:`, error);
        
        if (error instanceof Error && error.message.includes('quota')) {
          console.log('🔧 Applying storage optimization for unlimited capacity...');
          await this.optimizeStorage();
          
          if (retries < this.MAX_RETRIES) {
            console.log(`Retrying admin save (attempt ${retries + 1}/${this.MAX_RETRIES})...`);
            continue;
          }
        }
        
        if (retries >= this.MAX_RETRIES) {
          throw new Error(`Failed to add admin after ${this.MAX_RETRIES} attempts. Unlimited capacity optimization applied.`);
        }
      }
    }
    
    throw new Error('Failed to add admin - maximum retries exceeded');
  }

  async updateAdmin(id: string, updates: Partial<Admin>): Promise<Admin | null> {
    try {
      console.log(`💾 Updating admin ${id} with unlimited capacity...`);
      
      const allAdmins = await this.getAllAdmins();
      const adminIndex = allAdmins.findIndex(admin => admin.id === id);
      
      if (adminIndex === -1) {
        throw new Error(`Admin with ID ${id} not found`);
      }

      const updatedAdmin: Admin = {
        ...allAdmins[adminIndex],
        ...updates,
        id, // Preserve original ID
        updatedAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        syncStatus: 'synced'
      };

      allAdmins[adminIndex] = updatedAdmin;
      
      // Save with compression
      await this.saveAdminsWithCompression(allAdmins);
      
      // Update cache
      this.cache.set(id, updatedAdmin);
      
      console.log(`✅ Admin updated successfully: ${id}`);
      return updatedAdmin;
    } catch (error) {
      console.error(`Error updating admin ${id}:`, error);
      throw error;
    }
  }

  async deleteAdmin(id: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting admin ${id}...`);
      
      const allAdmins = await this.getAllAdmins();
      const filteredAdmins = allAdmins.filter(admin => admin.id !== id);
      
      if (filteredAdmins.length === allAdmins.length) {
        throw new Error(`Admin with ID ${id} not found`);
      }

      // Save with compression
      await this.saveAdminsWithCompression(filteredAdmins);
      
      // Remove from cache
      this.cache.delete(id);
      
      console.log(`✅ Admin deleted successfully: ${id}`);
    } catch (error) {
      console.error(`Error deleting admin ${id}:`, error);
      throw error;
    }
  }

  async batchAddAdmins(admins: Admin[]): Promise<Admin[]> {
    try {
      console.log(`📦 Batch adding ${admins.length} admins with unlimited capacity...`);
      
      const allAdmins = await this.getAllAdmins();
      const newAdmins: Admin[] = [];
      
      // Process in batches to avoid memory issues
      for (let i = 0; i < admins.length; i += this.BATCH_SIZE) {
        const batch = admins.slice(i, i + this.BATCH_SIZE);
        
        for (const admin of batch) {
          const newAdmin: Admin = {
            ...admin,
            id: admin.id || `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            createdAt: admin.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastModified: new Date().toISOString(),
            syncStatus: 'synced'
          };
          
          newAdmins.push(newAdmin);
          allAdmins.push(newAdmin);
        }
        
        console.log(`Processed batch ${Math.floor(i / this.BATCH_SIZE) + 1}/${Math.ceil(admins.length / this.BATCH_SIZE)}`);
      }
      
      // Save all at once with compression
      await this.saveAdminsWithCompression(allAdmins);
      
      // Update cache
      newAdmins.forEach(admin => this.cache.set(admin.id, admin));
      
      console.log(`✅ Batch added ${newAdmins.length} admins successfully`);
      return newAdmins;
    } catch (error) {
      console.error('Error in batch add admins:', error);
      throw error;
    }
  }

  private async saveAdminsWithCompression(admins: Admin[]): Promise<void> {
    try {
      // Apply compression for unlimited capacity
      const compressedData = storageCompression.compressData(admins);
      
      // Calculate compression efficiency
      const originalSize = JSON.stringify(admins).length;
      const compressedSize = compressedData.length;
      const ratio = storageCompression.calculateCompressionRatio(JSON.stringify(admins), compressedData);
      
      console.log(`📦 Compression: ${originalSize} → ${compressedSize} bytes (${ratio}% saved)`);
      
      await universalStorage.setItem(this.STORAGE_KEY, compressedData);
      
      console.log(`✅ Saved ${admins.length} admins with compression (unlimited capacity enabled)`);
    } catch (error) {
      console.error('Error saving compressed admin data:', error);
      
      // Fallback to direct save without compression
      console.log('🔄 Fallback: Saving without compression...');
      await universalStorage.setItem(this.STORAGE_KEY, admins);
    }
  }

  private async optimizeStorage(): Promise<void> {
    try {
      console.log('🔧 Optimizing storage for unlimited admin capacity...');
      
      // Clear cache to free memory
      this.clearCache();
      
      // Force garbage collection by clearing temporary variables
      if (global.gc) {
        global.gc();
      }
      
      // Reload and recompress data
      const admins = await this.getAllAdmins();
      await this.saveAdminsWithCompression(admins);
      
      console.log('✅ Storage optimization completed for unlimited capacity');
    } catch (error) {
      console.error('Storage optimization failed:', error);
    }
  }

  async getStorageStats(): Promise<{
    totalAdmins: number;
    storageUsed: string;
    maxCapacity: number;
    isOptimized: boolean;
    compressionRatio?: number;
  }> {
    try {
      const admins = await this.getAllAdmins();
      const rawData = JSON.stringify(admins);
      const compressedData = storageCompression.compressData(admins);
      
      const rawSize = rawData.length;
      const compressedSize = compressedData.length;
      const compressionRatio = storageCompression.calculateCompressionRatio(rawData, compressedData);
      
      return {
        totalAdmins: admins.length,
        storageUsed: `${(compressedSize / 1024).toFixed(2)} KB`,
        maxCapacity: 999999, // Unlimited with compression
        isOptimized: true,
        compressionRatio
      };
    } catch (error) {
      console.error('Error getting storage stats:', error);
      return {
        totalAdmins: 0,
        storageUsed: '0 KB',
        maxCapacity: 999999,
        isOptimized: false
      };
    }
  }

  private updateCache(admins: Admin[]): void {
    this.cache.clear();
    admins.forEach(admin => this.cache.set(admin.id, admin));
    this.cacheTimestamp = Date.now();
  }

  private isCacheValid(): boolean {
    return Date.now() - this.cacheTimestamp < this.CACHE_DURATION;
  }

  clearCache(): void {
    this.cache.clear();
    this.cacheTimestamp = 0;
  }

  async addAdminHistoryEntry(adminId: string, action: string, details: string): Promise<void> {
    try {
      const admin = await this.getAdminById(adminId);
      if (!admin) {
        throw new Error(`Admin with ID ${adminId} not found`);
      }

      const historyEntry: AdminHistory = {
        id: `hist_${uuidv4().substring(0, 8)}`,
        action,
        date: new Date().toISOString(),
        details
      };

      const updatedHistory = [...(admin.history || []), historyEntry];
      await this.updateAdmin(adminId, { history: updatedHistory });
    } catch (error) {
      console.error(`Error adding history entry for admin ${adminId}:`, error);
      throw error;
    }
  }
}

export const adminFileStorageService = new AdminFileStorageService();
